import type {
    CustomProduct,
    Product,
    Variant as ElevateVariant,
} from '@apptus/esales-api';
import type { Image, StructuredItem, Variant } from 'types';

import { SITE_CODE } from '@/config/common';
import type { MarketConfiguration } from '@/config/markets/const';

import getNorceCdnImageUrl from './getNorceCdnImageUrl';

export function getImagesOfProduct(
    product: CustomProduct,
    market: MarketConfiguration,
): Image[] {
    const primaryImages = product.imageInfo.images;
    const imageKeys = product.typedCustom?.json.imagesKey ?? [];
    const map = new Map<string, Image>();
    for (const image of primaryImages) {
        const [source] = image.sources;
        if (!source) continue;
        map.set(source.url, {
            src: source.url ?? '',
            alt: image.alt ?? image.caption ?? product.title,
            id: product.key + '_image',
            type: 'image',
        });
    }
    for (const imageKey of imageKeys) {
        if (map.has(imageKey)) continue;
        map.set(imageKey, {
            src: getNorceCdnImageUrl(imageKey, market),
            alt: product.title,
            id: product.key + '_image_' + imageKey,
            type: 'image',
        });
    }
    return [...map.values()];
}
export function getVatFromProduct(
    product: CustomProduct,
    market: MarketConfiguration,
) {
    const vatKey = `vat_${SITE_CODE}_${market.code}`;
    return product.typedCustom?.numbers[vatKey];
}

export function getPriceFromVariant(
    variant: ElevateVariant,
    market: MarketConfiguration,
): Pick<Variant, 'price' | 'priceType' | 'originalPrice' | 'campaignPrice'> {
    const priceKey = `${SITE_CODE}_${market.code}`;
    const prices = variant.prices.filter(({ id }) => id.includes(priceKey));
    if (prices) {
        if (prices.some((price) => price?.id.includes('campaign'))) {
            const [price] = prices;
            return {
                price: price.sellingPrice,
                priceType: 'campaign',
                originalPrice: price.listPrice,
                campaignPrice: true,
            };
        }
        if (prices.some((price) => price?.id.includes('pricematch'))) {
            const [price] = prices;
            return {
                price: price.sellingPrice,
                priceType: 'priceMatch',
                originalPrice: price.listPrice,
                campaignPrice: false,
            };
        }
        if (prices.some((price) => price?.id.includes('RRP'))) {
            const [price] = prices;
            return {
                price: price.sellingPrice,
                priceType: 'standard',
                originalPrice: undefined,
                campaignPrice: false,
            };
        }
        const [price] = prices;
        return {
            price: price.sellingPrice,
            priceType: 'standard',
            originalPrice: undefined,
            campaignPrice: false,
        };
    }
    return {
        price: variant.sellingPrice,
        priceType: 'standard',
        originalPrice: variant.listPrice,
        campaignPrice: false,
    };
}
export function getPriceFromProduct(
    product: CustomProduct,
    market: MarketConfiguration,
) {
    if (!product.typedCustom) return {};
    const priceKey = `${SITE_CODE}_${market.code}`;
    const priceKeys = Object.keys(product.typedCustom.numbers).filter((key) =>
        key.includes(priceKey),
    );
    const output: Record<string, number> = {};
    for (const id of priceKeys) {
        const [key] = id.split('_');
        output[key] = product.typedCustom.numbers[id] as number;
    }
    return output;
}
export function getVariantAttributes(product: CustomProduct) {
    const attributes =
        product.typedCustom?.json.variantDefiningVariantAttributes;
    if (!attributes) return {};
    const entries = Object.entries(attributes);
    return Object.fromEntries(
        entries.map(([key, value]) => [key, value.value]),
    );
}

function elevateProductToNgVariant(
    product: CustomProduct,
    marketConfig: MarketConfiguration,
): Variant {
    const {
        variants: [variant],
    } = product;
    const [image, ...images] = getImagesOfProduct(product, marketConfig);
    const variantPrice = getPriceFromVariant(variant, marketConfig);
    const productPrice = getPriceFromProduct(product, marketConfig);
    const variantAttributes = getVariantAttributes(product);
    const items: StructuredItem[] | undefined =
        product.typedCustom?.json.structureItems?.map((item) => ({
            partNo: item.PartNo ?? '',
            quantity: item.Quantity ?? 0,
            order: item.SortOrder ?? 0,
        }));

    const output: Variant = {
        id: variant.key,
        type: 'Variant',
        title: product.typedCustom?.json.variantName ?? product.title,
        image,
        images,
        groupId: product.key,
        nextDeliveryDate: new Date(product.custom?.nextDeliveryDate?.[0].id ?? ''),
        stock: Boolean(product.typedCustom?.numbers.stockOnHand),
        stockQuantity: product.typedCustom?.numbers.stockOnHand,
        articleStatus: product.custom?.itemStatus?.[0].id ?? '',
        stockStatus: product.custom?.stockStatus?.[0].id ?? '',
        lightsourceQuantity:
            product.typedCustom?.numbers.norce_light_source_quantity,
        flags: product.custom?.variantFlags?.map((flag) => flag.id),
        VariantDefiningAttributes: JSON.stringify(
            product.typedCustom?.json.variantDefiningVariantAttributes,
        ),
        deliveryTimeDays: product.typedCustom?.numbers.delivery_time_days,
        rrpWHenCampaign: productPrice.rrp,
        vatRate: productPrice.vat,
        link: variant.link,
        items,
        ...variantPrice,
        ...variantAttributes,
    };
    return output;
}

export default function mapElevateVariants(
    products: Product[],
    marketConfig: MarketConfiguration,
) {
    return products.map((p) => elevateProductToNgVariant(p, marketConfig));
}
